import { Checkbox, Collapse, Form, InputNumber, Select, Space, Tooltip, Typography } from "antd";
import { debounce } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ReactComponent as DividerIcon } from "../../assets/svg/Divider.svg";
import { selectedViews } from "../../constant/table";
import { GREEN, LIGHT_BLUE, NEW_BLUE, GRAY5, GRAY_400, WHITE } from "../../constant/colors";
import { FilterToolTip } from "../sharedComponents/FilterToolTip";
import {
  defaultFilters,
  FILTER_ENROLLMENT_ASSIGNEE,
  MainBucketsTrackEvents,
  UNASSIGNED_VALUE
} from "../../constant/filters";
import { formatAssignee, formatDrugsToDisplay } from "../../utils/formaters";
import { filterSort } from "../../utils/sort";
import { MultiSelect } from "../customComponent/CustomSelect";
import { useDashboardBucketsTrackingEvent } from "../../utils/pendo/hooks";
import { MultiSelectDrugSearch } from "@tailormed/common-client/component/MultiSelectDrugSearch";
import { DateRangeFilter } from "../filter/DateRangeFilter";
import { ALL_TIMES, tableFilterValues } from "../../constant/filters";
import {
  selectFilters,
  selectFiltersInputs,
  selectSelectedView,
  selectAPSponsors,
  selectUser
} from "../../store/selector";
import ACTIONS from "../../store/action";

const { Text, Title } = Typography;
const { Panel } = Collapse;
const { Option } = Select;

const ENROLLMENT_ASSIGNEE = "enrollmentAssignee";

// ✅ CENTRALIZED CONFIGURATION - Single source of truth for which filters show per view
const ENROLLMENT_FILTERS_CONFIG = {
  [selectedViews.allApplications]: {
    type: true,
    enrollmentStatus: true,
    apSponsor: true,
    enrollmentAssignee: true,
    drugName: true,
    doseFormCategory: true,
    enrollmentCreationDate: true,
    appliedDate: true,
    approvedDate: true,
    validThroughDate: true,
    signatureStatus: true,
    claimExists: true,
    maxAvailableFunds: true,
    createdByPatients: true
  },
  [selectedViews.pendingRenewal]: {
    type: true,
    enrollmentStatus: true,
    apSponsor: true,
    enrollmentAssignee: true,
    drugName: true,
    doseFormCategory: true,
    enrollmentCreationDate: true,
    appliedDate: true,
    approvedDate: true,
    validThroughDate: true,
    signatureStatus: false,
    claimExists: true,
    maxAvailableFunds: true,
    createdByPatients: true
  },
  [selectedViews.patientActions]: {
    type: false,
    enrollmentStatus: true,
    apSponsor: true,
    enrollmentAssignee: false,
    drugName: true,
    doseFormCategory: false,
    enrollmentCreationDate: false,
    appliedDate: false,
    approvedDate: false, // TODO: change to true when MVP-28973 is resolved
    validThroughDate: false,
    signatureStatus: false,
    claimExists: false,
    maxAvailableFunds: false,
    createdByPatients: false
  },
  [selectedViews.allClaims]: {
    type: true,
    enrollmentStatus: true,
    apSponsor: true,
    enrollmentAssignee: true,
    drugName: true,
    doseFormCategory: true,
    enrollmentCreationDate: true,
    appliedDate: true,
    approvedDate: true,
    validThroughDate: true,
    signatureStatus: false,
    claimExists: false, // Don't show "claim exists" filter in All Claims view
    maxAvailableFunds: true,
    createdByPatients: true
  }
};

const StyledGreenDot = () => (
  <div
    style={{
      position: "absolute",
      background: GREEN,
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      left: "247px",
      top: "24px"
    }}
  />
);

export const EnrollmentFiltersPanel = ({
  handleInputMultiSelect,
  handleInputDate,
  handleInputSingleSelect,
  handleInputNumber,
  handleCheckbox,
  resetDatesToDefault,
  filtersVisible,
  withSignatureSubscription,
  claimsEnabled,
  shouldShowEnrollmentCreatedByPatientsFilter,
  form,
  assigneesTextToDisplay,
  validAssignees,
  assignees,
  searchRxnormDrugs,
  handleDrugFilterChange, // Generic handler that takes (filterKey, selectedDrugs)
  enrollmentDrugsFilterKey = "rxnormDrugs" // Which filter to use (default: rxnormDrugs, override: potentialSavingsRxnormDrugs)
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { trackEvent } = useDashboardBucketsTrackingEvent();
  const filters = useSelector(selectFilters);
  const filtersInputs = useSelector(selectFiltersInputs);
  const selectedView = useSelector(selectSelectedView);
  const apSponsors = useSelector(selectAPSponsors);
  const user = useSelector(selectUser);

  useEffect(() => {
    if (!apSponsors?.length) {
      dispatch(ACTIONS.fetchAPSponsors());
    }
  }, []);

  const { dates, multiples, numbers } = filters;

  const setFiltersInputs = (filterName, value) => dispatch(ACTIONS.setFiltersInputs(filterName, value));
  const setFiltersAct = (filters) => dispatch(ACTIONS.setFiltersAction(filters));

  // State for enrollment assignee search
  const enrollmentAssigneesSearchInputRef = useRef("");

  // State for enrollment drug filter tooltip
  const [isDrugNameSelectOpen, setIsDrugNameSelectOpen] = useState(false);
  const [isDrugNameSelectHover, setIsDrugNameSelectHover] = useState(false);

  const handleEnrollmentAssigneeChange = (values, options) => {
    const isUnassignedEnrollment = values.includes(UNASSIGNED_VALUE);
    const assigneeValues = values.filter((v) => v !== UNASSIGNED_VALUE);
    const assigneeOptions = options.filter((o) => o.value !== UNASSIGNED_VALUE);

    const filterValues = {};
    assigneeOptions.forEach((option, index) => {
      // The "(Me)" option renders JSX children that are not serializable — extract plain text for saved list persistence
      filterValues[index] = {
        value: option.value,
        text:
          typeof option.children === "string"
            ? option.children
            : formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" })
      };
    });

    const wasMe = filtersInputs[ENROLLMENT_ASSIGNEE]?.includes(user?.id);
    const wasUnassigned = filters.isUnassignedEnrollment;
    if (values.includes(user?.id) && !wasMe && !enrollmentAssigneesSearchInputRef.current) {
      trackEvent(MainBucketsTrackEvents.FILTER_ASSIGNEE_ME_OPTION_SELECTED);
    }
    if (isUnassignedEnrollment && !wasUnassigned) {
      trackEvent(MainBucketsTrackEvents.FILTER_ASSIGNEE_UNASSIGNED_OPTION_SELECTED);
    }

    setFiltersInputs("isUnassignedEnrollment", isUnassignedEnrollment);
    setFiltersInputs(ENROLLMENT_ASSIGNEE, assigneeValues);
    setFiltersAct({
      ...filters,
      isUnassignedEnrollment,
      multiples: { ...multiples, [ENROLLMENT_ASSIGNEE]: filterValues }
    });
  };

  // Get the filter configuration for current view
  const filterConfig = ENROLLMENT_FILTERS_CONFIG[selectedView];

  // Don't show component if view not configured
  if (!filterConfig) return null;

  // Determine status options based on view
  const statusOptions =
    selectedView === selectedViews.pendingRenewal
      ? tableFilterValues.pendingRenewalAPStatuses
      : tableFilterValues.allApplicationsAPStatuses;

  // Determine program type options based on view
  const applicationTypeFilterOptions = tableFilterValues.potentialSavingsTypes;

  // Determine date filter name for validThroughDate (different filters per view)
  const dateFilterName =
    selectedView === selectedViews.allApplications
      ? "validThroughDateRangeAllApplicationFilters"
      : "validThroughDateRangeFilters";

  const isEnrollmentFiltersActive = () => {
    const typeSelected = multiples?.potentialSavingsTypes && Object.keys(multiples.potentialSavingsTypes).length > 0;
    const statusSelected =
      multiples?.potentialSavingsAplicationStatuses &&
      Object.keys(multiples.potentialSavingsAplicationStatuses).length > 0;
    const sponsorSelected =
      multiples?.potentialSavingsAPSponsor && Object.keys(multiples.potentialSavingsAPSponsor).length > 0;

    // Use enrollmentDrugsFilterKey to check if any drugs are selected
    const drugSelected = filtersInputs?.[enrollmentDrugsFilterKey]?.length > 0;

    const doseFormSelected =
      multiples?.potentialSavingsDrugDoseFormCategories &&
      Object.keys(multiples.potentialSavingsDrugDoseFormCategories).length > 0;
    const assigneeSelected = multiples?.enrollmentAssignee && Object.keys(multiples.enrollmentAssignee).length > 0;
    const appliedDateValue = dates?.appliedDateRange?.value;
    const approvedDateValue = dates?.approvedDateRange?.value;
    const validThroughDateValue = dates?.validThroughDateRange?.value;
    const signatureSelected =
      filterConfig.signatureStatus &&
      multiples?.applicationSignatureStatuses &&
      Object.keys(multiples.applicationSignatureStatuses).length > 0;
    const claimExists = filters.claimExists;
    const { applicationFundBalance } = numbers || {};
    const enrollmentsCreatedByPatients = filters?.enrollmentsCreatedByPatients;
    const isUnassignedEnrollment = filtersInputs.isUnassignedEnrollment;
    const enrollmentCreationDateValue = dates?.enrollmentCreationDateRange?.value;

    return (
      typeSelected ||
      statusSelected ||
      sponsorSelected ||
      drugSelected ||
      doseFormSelected ||
      assigneeSelected ||
      signatureSelected ||
      (enrollmentCreationDateValue && enrollmentCreationDateValue !== ALL_TIMES) ||
      appliedDateValue !== ALL_TIMES ||
      approvedDateValue !== ALL_TIMES ||
      validThroughDateValue !== ALL_TIMES ||
      claimExists !== null ||
      (applicationFundBalance && applicationFundBalance !== "0") ||
      Boolean(enrollmentsCreatedByPatients) ||
      !!isUnassignedEnrollment
    );
  };

  // Render drug name filter - uses enrollmentDrugsFilterKey to determine which filter to read/write
  const renderDrugNameFilter = () => {
    // Get drug value from filtersInputs using the key
    const drugValue = filtersInputs[enrollmentDrugsFilterKey] || [];
    const drugNameTextToDisplay = formatDrugsToDisplay(drugValue);

    return (
      <Space size={0} style={{ width: "100%" }} direction="vertical">
        <Text strong style={{ color: LIGHT_BLUE }}>
          {t("tableFilters.application_drug_name")}
        </Text>
        <Tooltip
          open={drugNameTextToDisplay && !isDrugNameSelectOpen && isDrugNameSelectHover}
          title={<Text>{drugNameTextToDisplay}</Text>}
          color={WHITE}
          placement="right"
          overlayStyle={{
            maxWidth: "calc(100% - 600px)",
            whiteSpace: "normal",
            wordWrap: "break-word"
          }}
        >
          <MultiSelectDrugSearch
            id="drug_filter_name"
            searchRxnormDrugs={searchRxnormDrugs}
            getPopupContainer={(trigger) => trigger.parentNode}
            mode="multiple"
            placeholder={t("all")}
            onChangeSelectedDrugs={(selectedDrugs) => handleDrugFilterChange(enrollmentDrugsFilterKey, selectedDrugs)}
            onClear={() => handleDrugFilterChange(enrollmentDrugsFilterKey, [])}
            value={drugValue}
            displayNameMaxChars={25}
            bordered={false}
            style={{
              width: "100%",
              border: `0px`,
              borderBottom: `1px solid ${GRAY5}`,
              padding: 0
            }}
            onDropdownVisibleChange={(open) => setIsDrugNameSelectOpen(open)}
            onMouseEnter={() => setIsDrugNameSelectHover(true)}
            onMouseLeave={() => setIsDrugNameSelectHover(false)}
            maxTagCount={1}
            maxTagTextLength={drugValue?.length == 1 ? 15 : 6}
          />
        </Tooltip>
      </Space>
    );
  };

  return (
    <Collapse ghost expandIconPosition="end" style={{ display: !filtersVisible ? "none" : undefined }}>
      <Panel
        header={
          <Title level={5} style={{ color: NEW_BLUE }}>
            {t("tableFilters.enrollments")}
            {isEnrollmentFiltersActive() && <StyledGreenDot />}
          </Title>
        }
      >
        <Space size={16} direction="vertical" style={{ width: "100%" }}>
          {/* Type (Program Type) */}
          {filterConfig.type && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <div>
                <Text strong style={{ color: LIGHT_BLUE }}>
                  {t("tableFilters.type")}
                </Text>
                {selectedView !== selectedViews.allClaims && (
                  <FilterToolTip
                    title={t("manufacturer_other_tooltip")}
                    toolTipId="type_filter_popup"
                    iconId="type_filter_popup_icon"
                    iconStyles={{ left: "10px" }}
                  />
                )}
              </div>
              <MultiSelect
                id="potential_savings_filter_type"
                onChange={(values, options) => handleInputMultiSelect(values, options, "potentialSavingsTypes")}
                tooltipContent={Object.values(multiples.potentialSavingsTypes || {})}
                value={filtersInputs.potentialSavingsTypes}
                mode="multiple"
                maxTagCount={1}
                maxTagTextLength={filtersInputs.potentialSavingsTypes?.length > 1 ? 8 : 16}
                allowClear={true}
                style={{ width: "100%" }}
                placeholder={t("all")}
                bordered={false}
                filterOption={(inputValue, option) => option.children.toLowerCase().includes(inputValue?.toLowerCase())}
              >
                {applicationTypeFilterOptions.map((option) => (
                  <Option
                    id={`potential_savings_filter_type_${option.translationText}`}
                    key={option.key}
                    value={option.value}
                  >
                    {t(option.translationText)}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
          )}

          {/* Enrollment Status */}
          {filterConfig.enrollmentStatus && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.enrollment_status")}
              </Text>
              <MultiSelect
                id="potential_savings_filter_application_status"
                onChange={(values, options) =>
                  handleInputMultiSelect(values, options, "potentialSavingsAplicationStatuses")
                }
                tooltipContent={Object.values(multiples.potentialSavingsAplicationStatuses || {})}
                value={filtersInputs.potentialSavingsAplicationStatuses}
                mode="multiple"
                maxTagCount={1}
                autoClearSearchValue={false}
                maxTagTextLength={filtersInputs.potentialSavingsAplicationStatuses?.length > 1 ? 9 : 18}
                allowClear={true}
                style={{ width: "100%" }}
                placeholder={t("all")}
                bordered={false}
                filterSort={filterSort}
              >
                {statusOptions.map((option, index) => (
                  <Option key={index} value={option.value} localSortKey={option.localSortKey.toString()}>
                    {t(option.translationText)}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
          )}

          {/* AP Sponsor */}
          {filterConfig.apSponsor && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.ap_sponsor")}
              </Text>
              <MultiSelect
                id="potential_savings_filter_ap_sponsor"
                onChange={(values, options) => handleInputMultiSelect(values, options, "potentialSavingsAPSponsor")}
                tooltipContent={Object.values(multiples.potentialSavingsAPSponsor || {})}
                value={filtersInputs.potentialSavingsAPSponsor}
                mode="multiple"
                maxTagCount={1}
                maxTagTextLength={filtersInputs.potentialSavingsAPSponsor?.length > 1 ? 8 : 16}
                allowClear={true}
                style={{ width: "100%" }}
                placeholder={t("all")}
                bordered={false}
              >
                {apSponsors?.map((option, index) => (
                  <Option key={index} value={option}>
                    {option}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
          )}

          {/* Enrollment Assignee */}
          {filterConfig.enrollmentAssignee && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.enrollment_assignee")}
              </Text>
              <MultiSelect
                id={FILTER_ENROLLMENT_ASSIGNEE}
                tooltipContent={assigneesTextToDisplay(ENROLLMENT_ASSIGNEE)}
                onChange={handleEnrollmentAssigneeChange}
                value={[
                  ...(filtersInputs.isUnassignedEnrollment ? [UNASSIGNED_VALUE] : []),
                  ...(!validAssignees(ENROLLMENT_ASSIGNEE)
                    ? []
                    : validAssignees(ENROLLMENT_ASSIGNEE).map(({ id }) => id))
                ]}
                autoClearSearchValue={false}
                showSearch
                mode="multiple"
                maxTagCount={1}
                maxTagTextLength={filtersInputs.enrollmentAssignee?.length > 1 ? 8 : 16}
                allowClear
                style={{ width: "100%" }}
                placeholder={`${t("all")}`}
                onSearch={(value) => {
                  enrollmentAssigneesSearchInputRef.current = value.toLowerCase();
                }}
                onBlur={() => {
                  enrollmentAssigneesSearchInputRef.current = "";
                }}
                filterOption={(input, option) => {
                  if (option.value === user?.id) {
                    return (
                      !input ||
                      formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" })
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    );
                  }
                  if (option.localSortKey === "") return !input;
                  return option.children?.toLowerCase?.().indexOf(input.toLowerCase()) >= 0;
                }}
                filterSort={(a, b) => {
                  const aPinned = a.localSortKey === "";
                  const bPinned = b.localSortKey === "";
                  if (aPinned && !bPinned) return -1;
                  if (bPinned && !aPinned) return 1;
                  if (aPinned && bPinned) return 0;
                  const aIsSelected = filtersInputs.enrollmentAssignee?.indexOf(a.value) > -1;
                  const bIsSelected = filtersInputs.enrollmentAssignee?.indexOf(b.value) > -1;
                  if ((aIsSelected && bIsSelected) || (!aIsSelected && !bIsSelected)) {
                    return a.children.localeCompare(b.children);
                  }
                  if (aIsSelected && !bIsSelected) {
                    return -1;
                  }
                  if (!aIsSelected && bIsSelected) {
                    return 1;
                  }
                }}
                bordered={false}
                onSelectAll={() => {
                  const filteredAssignees = assignees.filter((d) =>
                    d.displayName?.toLowerCase().includes(enrollmentAssigneesSearchInputRef.current)
                  );
                  const values = filteredAssignees.map(({ id }) => id);
                  const options = assignees.map(({ id, displayName, name }, index) => ({
                    value: id,
                    key: index,
                    children: displayName || name
                  }));

                  handleInputMultiSelect(values, options, ENROLLMENT_ASSIGNEE);
                }}
                onClearAll={() => {
                  setFiltersInputs("isUnassignedEnrollment", false);
                  setFiltersInputs(ENROLLMENT_ASSIGNEE, []);
                  setFiltersAct({
                    ...filters,
                    isUnassignedEnrollment: false,
                    multiples: { ...multiples, [ENROLLMENT_ASSIGNEE]: {} }
                  });
                  enrollmentAssigneesSearchInputRef.current = "";
                }}
                selectAll
                clearAll
                optionLabelProp="displayName"
              >
                {user && assignees.some(({ id }) => id === user.id) && (
                  <Option
                    key="me"
                    value={user.id}
                    localSortKey=""
                    displayName={formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" })}
                    id="enrollment_assignee_filter_me"
                  >
                    {formatAssignee({ firstName: user.firstName, lastName: user.lastName, type: "user" })}{" "}
                    <span style={{ color: GRAY_400 }}>{t("bulk_actions.current_user_suffix")}</span>
                  </Option>
                )}
                <Option
                  key="unassigned"
                  value={UNASSIGNED_VALUE}
                  localSortKey=""
                  displayName={t("unassigned")}
                  id="enrollment_assignee_filter_unassigned"
                >
                  {t("unassigned")}
                </Option>
                <Option
                  key="assignee-divider"
                  disabled
                  localSortKey=""
                  style={{ padding: "4px 8px", minHeight: 0, lineHeight: 0, fontSize: 0, cursor: "default" }}
                >
                  <DividerIcon style={{ width: "100%" }} />
                </Option>
                {assignees
                  .filter(({ id }) => id !== user?.id)
                  .map(({ id, firstName, lastName, name, type }) => (
                    <Option key={id} value={id} displayName={formatAssignee({ firstName, lastName, name, type })}>
                      {formatAssignee({ firstName, lastName, name, type })}
                    </Option>
                  ))}
              </MultiSelect>
            </Space>
          )}

          {/* Drug Name */}
          {filterConfig.drugName && renderDrugNameFilter()}

          {/* Dose Form Category */}
          {filterConfig.doseFormCategory && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.dose_form_category")}
              </Text>
              <MultiSelect
                id="drug_dose_form_category_box"
                onChange={(values, options) =>
                  handleInputMultiSelect(values, options, "potentialSavingsDrugDoseFormCategories")
                }
                tooltipContent={Object.values(multiples.potentialSavingsDrugDoseFormCategories || {})}
                value={filtersInputs.potentialSavingsDrugDoseFormCategories}
                mode="multiple"
                maxTagCount={1}
                maxTagTextLength={filtersInputs.potentialSavingsDrugDoseFormCategories?.length > 1 ? 8 : 16}
                allowClear={true}
                style={{ width: "100%" }}
                placeholder={t("all")}
                bordered={false}
              >
                {tableFilterValues.drugDoseFormCategory.map((option) => (
                  <Option
                    id={`drug_dose_form_category_filter_${option.translationText}`}
                    key={option.key}
                    value={option.value}
                  >
                    {t(option.translationText)}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
          )}

          {/* Enrollment Creation Date */}
          {filterConfig.enrollmentCreationDate && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.enrollment_creation_date")}
              </Text>
              <DateRangeFilter
                id="enrollment_filter_created_date"
                handleInput={(value = ALL_TIMES) => setFiltersInputs("enrollmentCreationDateRange", value)}
                onSelect={(fromDate, toDate, value = null) =>
                  handleInputDate(fromDate, toDate, "enrollmentCreationDateRange", value)
                }
                defaultValue={ALL_TIMES}
                placeHolder={t(ALL_TIMES)}
                dateFilters="enrollmentCreationDateFilters"
                timeRange={dates?.enrollmentCreationDateRange}
                allowClear={true}
                onClear={() => resetDatesToDefault("enrollmentCreationDateRange", ALL_TIMES)}
              />
            </Space>
          )}

          {/* Applied Date */}
          {filterConfig.appliedDate && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.applied_date")}
              </Text>
              <DateRangeFilter
                id="applied_date_input"
                handleInput={(value = ALL_TIMES) => setFiltersInputs("appliedDateRange", value)}
                onSelect={(fromDate, toDate, value = null) =>
                  handleInputDate(fromDate, toDate, "appliedDateRange", value)
                }
                defaultValue={ALL_TIMES}
                placeHolder={t(ALL_TIMES)}
                dateFilters="appliedDateFilters"
                timeRange={dates?.appliedDateRange}
                allowClear={true}
                onClear={() => resetDatesToDefault("appliedDateRange", ALL_TIMES)}
              />
            </Space>
          )}

          {/* Approved Date */}
          {filterConfig.approvedDate && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.approved_date")}
              </Text>
              <DateRangeFilter
                id="approved_date_input"
                handleInput={(value = ALL_TIMES) => setFiltersInputs("approvedDateRange", value)}
                onSelect={(fromDate, toDate, value = null) =>
                  handleInputDate(fromDate, toDate, "approvedDateRange", value)
                }
                defaultValue={ALL_TIMES}
                placeHolder={t(ALL_TIMES)}
                dateFilters="approvedDateFilters"
                timeRange={dates?.approvedDateRange}
                allowClear={true}
                onClear={() => resetDatesToDefault("approvedDateRange", ALL_TIMES)}
              />
            </Space>
          )}

          {/* Valid Through Date */}
          {filterConfig.validThroughDate && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.valid_through_date")}
              </Text>
              <DateRangeFilter
                id="valid_through_date_input"
                handleInput={(value = ALL_TIMES) => setFiltersInputs("validThroughDateRange", value)}
                onSelect={(fromDate, toDate, value = null) =>
                  handleInputDate(fromDate, toDate, "validThroughDateRange", value)
                }
                defaultValue={ALL_TIMES}
                placeHolder={t(ALL_TIMES)}
                dateFilters={dateFilterName}
                timeRange={dates?.validThroughDateRange}
                allowClear={true}
                onClear={() => resetDatesToDefault("validThroughDateRange", ALL_TIMES)}
              />
            </Space>
          )}

          {/* Signature Status */}
          {filterConfig.signatureStatus && withSignatureSubscription && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.signature_status")}
              </Text>
              <MultiSelect
                id="application_signature_statuses_filter"
                onChange={(values, options) => handleInputMultiSelect(values, options, "applicationSignatureStatuses")}
                tooltipContent={Object.values(multiples.applicationSignatureStatuses || {})}
                value={filtersInputs.applicationSignatureStatuses}
                mode="multiple"
                maxTagCount={1}
                maxTagTextLength={filtersInputs.applicationSignatureStatuses?.length > 1 ? 8 : 16}
                allowClear={true}
                style={{ width: "100%" }}
                placeholder={t("all")}
                bordered={false}
              >
                {tableFilterValues.applicationSignatureStatuses.map((option, index) => (
                  <Option key={index} value={option.value}>
                    {t(option.translationText)}
                  </Option>
                ))}
              </MultiSelect>
            </Space>
          )}

          {/* Claim Exists */}
          {filterConfig.claimExists && claimsEnabled && selectedView !== selectedViews.allClaims && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <div>
                <Text strong style={{ color: LIGHT_BLUE }}>
                  {t("tableFilters.claim_exists.text")}
                </Text>
                <FilterToolTip
                  title={t("tableFilters.claim_exists.pop_up_content")}
                  toolTipId="claims_exist_filter_popup"
                  iconId="claims_exist_filter_popup_icon"
                  iconStyles={{ left: "10px" }}
                />
              </div>
              <Select
                id="filter_by_claim_exists"
                onSelect={(values, option) => {
                  handleInputSingleSelect(values, option, "claimExists");
                }}
                onClear={() => {
                  handleInputSingleSelect(defaultFilters.claimExists, null, "claimExists");
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
                value={filters.claimExists}
                allowClear={true}
                style={{ width: "100%", borderBottom: `1px solid ${GRAY5}` }}
                placeholder={t("all")}
                bordered={false}
              >
                {tableFilterValues.claimExists.map((option) => {
                  const id = `filter_by_claim_exists_${option.translationText}`;
                  return (
                    <Option id={id} key={option.key} value={option.value}>
                      {t(option.translationText)}
                    </Option>
                  );
                })}
              </Select>
            </Space>
          )}

          {/* Max Available Funds */}
          {filterConfig.maxAvailableFunds && (
            <Space size={0} style={{ width: "100%" }} direction="vertical">
              <Text strong style={{ color: LIGHT_BLUE }}>
                {t("tableFilters.max_available_funds")}
              </Text>
              <Form form={form}>
                <Form.Item
                  name="applicationFundBalance"
                  rules={[{ required: false }]}
                  initialValue={numbers.applicationFundBalance ? numbers.applicationFundBalance : 0}
                >
                  <InputNumber
                    id="application_fund_balance"
                    onChange={debounce(
                      (value) => handleInputNumber(value && value != "0" ? value : 0, "applicationFundBalance"),
                      500
                    )}
                    controls={false}
                    addonAfter="$"
                    defaultValue="0"
                    formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  />
                </Form.Item>
              </Form>
            </Space>
          )}

          {/* Show only enrollments created by patients */}
          {filterConfig.createdByPatients && shouldShowEnrollmentCreatedByPatientsFilter && (
            <Space id="filter_by_patients_enrollment">
              <Checkbox
                style={{ marginLeft: "0px" }}
                checked={filters.enrollmentsCreatedByPatients}
                onChange={(e) => handleCheckbox(e, "enrollmentsCreatedByPatients")}
              >
                <Text strong style={{ color: LIGHT_BLUE, paddingLeft: 4 }}>
                  {t("tableFilters.created_by_patients")}
                </Text>
              </Checkbox>
            </Space>
          )}
        </Space>
      </Panel>
    </Collapse>
  );
};
